import type { ComponentType, SVGProps } from "react";
import {
  DATASET_FACET_UNSPECIFIED_TOKENS,
  EPISODE_LABEL_KEYS,
  type ArmLayout,
  type Embodiment,
  type Label,
  type LabelCategory,
  type Tip,
} from "@/api";
import {
  ACTION_SOURCE_OPTIONS,
  HARDWARE_AXIS_KEYS,
  episodeQualityTagText,
  episodeQualityTagTip,
  episodeTagChoices,
  labelExplanation,
  labelGuideSummary,
  labelValueText,
  sourceLabelValues,
} from "@/components/label";
import {
  BeakerIcon,
  CameraIcon,
  CodeIcon,
  MoveIcon,
  ToolsIcon,
  formatColorPickerColorHex,
  type FilterGroup,
  type FilterOption,
  type FilterSelection,
  type SearchOption,
} from "@carbon6/c6-frontend-design";

// 전용 facet 파라미터(콤마 IN)다. backend label_filters.py 와 1:1 로 대응한다. 이 key 의 선택은
// generic 라벨이 아니라 전용 query 파라미터로 보낸다. 대상은 라벨 facet 3종,
// embodiment/camera_channel/camera_count/tip facet, 에피소드 결함·행동 출처 축이다.
export const DATASET_FACET_PARAM_KEYS = [
  "environment",
  "source",
  "task",
  "embodiment",
  "arms",
  "end_effector",
  "camera_channel",
  "camera_count",
  "tip",
  "recording_mode",
  "episode_quality",
  "episode_quality_candidate",
  "action_source",
] as const;

export type DatasetFacetParamKey = (typeof DATASET_FACET_PARAM_KEYS)[number];

export function isDatasetFacetParamKey(key: string): key is DatasetFacetParamKey {
  return (DATASET_FACET_PARAM_KEYS as readonly string[]).includes(key);
}

// 필터 바에 표시하는 facet 의 SoT 다. 순서·섹션·표시 라벨을 여기서만 정한다. 여기에 없는 key(camera
// 기기명·generic 라벨 등)는 드롭다운에 표시하지 않는다(generic 라벨은 라벨 토큰 검색이 담당).
//   preset   = 프리셋(빠른 탐색)으로만 조작한다. 카탈로그에 로드하지만 컨트롤은 렌더하지 않는다(environment).
//   quick    = 빠른 탐색 영역에 두는 축(Task·Embodiment).
//   advanced = 접이식 고급 필터(데이터 소스·하드웨어/품질).
export type FacetSection = "preset" | "quick" | "advanced";
export type CuratedFacet = { key: string; label: string; section: FacetSection };

export const CURATED_FACETS: readonly CuratedFacet[] = [
  // environment 는 컨트롤을 렌더하지 않고 프리셋(RealWorld/시뮬레이션)으로만 조작한다.
  // 단 프리셋 선택을 유지하려면(FilterController.normalizeSelection) 그룹을 카탈로그에 로드해야 한다.
  { key: "environment", label: "환경", section: "preset" },
  { key: "task", label: "작업", section: "quick" },
  { key: "embodiment", label: "Embodiment", section: "quick" },
  { key: "source", label: "데이터 소스", section: "advanced" },
  { key: "recording_mode", label: "수집 방식", section: "advanced" },
  // arms 는 environment 처럼 프리셋(양팔/한팔)으로만 조작한다. 전용 컨트롤(ContentSwitcher)은
  // 프리셋과 중복이라 두지 않는다. 그래서 미판정은 필터할 수 없다(제품 결정). 라벨은 적용 조건
  // 칩("팔 구성: …") 표기에 쓴다.
  { key: "arms", label: "팔 구성", section: "preset" },
  { key: "camera_channel", label: "Camera Layout", section: "advanced" },
  { key: "camera_count", label: "카메라 수", section: "advanced" },
  { key: "end_effector", label: "End effector", section: "advanced" },
  { key: "tip", label: "Tip", section: "advanced" },
  { key: "episode_quality_candidate", label: "미처리 품질 후보", section: "advanced" },
  { key: "episode_quality", label: "에피소드 결함", section: "advanced" },
  { key: "action_source", label: "행동 출처", section: "advanced" },
];

const facetSectionByKey = new Map<string, FacetSection>(
  CURATED_FACETS.map((facet) => [facet.key, facet.section]),
);

// facet key 가 속한 섹션이다. 없으면 null 이고, 표시 대상이 아니다. DatasetFilter 가 섹션별 렌더에 쓴다.
export function facetSection(key: string): FacetSection | null {
  return facetSectionByKey.get(key) ?? null;
}

// 빌더가 만든 후보 그룹 union 을 CURATED_FACETS 순서로 고르고 표시 라벨을 덮어쓴다. 스펙에 없는 key 와
// 옵션이 0개인 그룹은 버린다(source 는 고정 vocab 이라 항상 남는다). 이 결과를 카탈로그로 로드한다.
export function selectCuratedFacetGroups(groups: readonly FilterGroup[]): readonly FilterGroup[] {
  const byKey = new Map(groups.map((group) => [group.value, group] as const));
  const selected: FilterGroup[] = [];
  for (const facet of CURATED_FACETS) {
    const group = byKey.get(facet.key);
    if (group && group.filters.length > 0) {
      selected.push({ ...group, label: facet.label });
    }
  }
  return selected;
}

// 빠른 탐색(use-case) 프리셋이다. 자주 쓰는 조합을 한 번에 적용하는 단축키다. 각 칩은 하위 facet
// 선택을 설정한다. 새 상태가 아니라 facet 선택으로 변환되므로 컨트롤러와 쿼리 계층을 바꾸지 않고
// 동작한다. Icon 은 c6 글리프 컴포넌트이고, DatasetFilter 가 currentColor 아이콘으로 렌더한다.
// c6 에 팔/로봇 글리프가 없어서 비슷한 아이콘을 쓴다.
export type DatasetFilterPreset = {
  id: string;
  label: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  // 이 프리셋이 설정하는 facet 선택이다(예: { arms: ["bimanual"] }). key 는 CURATED_FACETS 의 facet key 다.
  selection: Readonly<Record<string, readonly string[]>>;
};

export const DATASET_FILTER_PRESETS: readonly DatasetFilterPreset[] = [
  {
    id: "bimanual",
    label: "Bimanual 조작",
    Icon: ToolsIcon,
    selection: { arms: ["bimanual"] },
  },
  { id: "single", label: "Single Arm 조작", Icon: MoveIcon, selection: { arms: ["single_arm"] } },
  { id: "real", label: "RealWorld 데이터", Icon: CameraIcon, selection: { environment: ["real"] } },
  {
    id: "sim",
    label: "시뮬레이션 데이터",
    Icon: BeakerIcon,
    selection: { environment: ["simulation"] },
  },
  // source 는 고정 vocab 정적 그룹이라 항상 로드된다. 그래서 프리셋 대상 facet 으로 써도 안전하다.
  {
    id: "open_source",
    label: "오픈소스 데이터",
    Icon: CodeIcon,
    selection: { source: ["open_source"] },
  },
];

// 프리셋이 활성인지 판정한다. 프리셋의 모든 facet 값이 현재 선택에 포함되면 활성이다(선택에서 계산한 활성).
// 축이 겹치지 않는 프리셋(양팔 + Real 등)은 동시에 활성일 수 있다.
export function isPresetActive(preset: DatasetFilterPreset, selection: FilterSelection): boolean {
  return Object.entries(preset.selection).every(([key, values]) => {
    const current = selection[key] ?? [];
    return values.length > 0 && values.every((value) => current.includes(value));
  });
}

// 프리셋을 지금 조작할 수 있는지 판정한다. 대상 facet 그룹이 모두 카탈로그에 로드되었을 때만 참이다.
// 로드되지 않았으면 선택이 유지되지 않아(FilterController.normalizeSelection) 눌러도 동작하지 않는다.
// 그래서 비활성화한다. 예: environment 라벨이 없는 목록에서는 RealWorld/시뮬레이션 프리셋을
// 비활성화해서 없는 축을 적용하지 못하게 한다.
export function isPresetAvailable(
  preset: DatasetFilterPreset,
  loadedGroupValues: Iterable<string>,
): boolean {
  const loaded = new Set(loadedGroupValues);
  return Object.keys(preset.selection).every((key) => loaded.has(key));
}

// 라벨 조건 후보다. 검색 콤보박스 드롭다운과 고급 필터 시트의 토큰 입력이 같은 목록을 쓴다.
// c6 SearchOption(value·label·description·accentColor)에 건수를 추가한 형태라서 시트의
// TokenSearchInput 에 그대로 전달한다.
export type LabelConditionOption = SearchOption & {
  // 이 라벨이 붙은 available 데이터셋 수다. embedded projection 에는 없어서 optional 이다.
  count?: number;
  // 라벨 색(0xRRGGBB)이다. 후보 행의 칩이 쓴다. `accentColor` 는 같은 색의 hex 문자열이다(c6 토큰 입력용).
  color: number;
};

// facet 전용 key(예약·하드웨어)를 뺀 generic 라벨만 key:value 조건으로 표시한다. 환경/소스/
// 태스크/embodiment/camera 등은 전용 facet 드롭다운이 담당한다. 선택한 조건은 컨트롤러가 facet
// 선택과 병합해 labels_all 로 변환한다.
// 에피소드 전용 key 를 데이터셋 라벨 조건으로 고르면 항상 0건이다. 에피소드 결함은
// `episode_quality` facet 이 담당한다.
const LABEL_TOKEN_EXCLUDED_KEYS: ReadonlySet<string> = new Set([
  ...DATASET_FACET_PARAM_KEYS,
  ...HARDWARE_AXIS_KEYS,
  ...EPISODE_LABEL_KEYS,
]);

export function createLabelTokenOptions(labels: readonly Label[]): readonly LabelConditionOption[] {
  const seen = new Set<string>();
  const options: LabelConditionOption[] = [];
  for (const label of labels) {
    if (LABEL_TOKEN_EXCLUDED_KEYS.has(label.key)) {
      continue;
    }
    const value = `${label.key}:${label.value}`;
    if (seen.has(value)) {
      continue;
    }
    seen.add(value);
    options.push({
      value,
      label: labelTokenDisplay(label.key, label.value),
      // 설명은 서버 description → FE 가이드 → key 순서로 고른다. labelExplanation 규칙이고, 마지막 폴백만 여기서 정한다.
      description: labelExplanation(label) ?? label.key,
      accentColor: formatColorPickerColorHex(label.color),
      color: label.color,
      count: label.dataset_count,
    });
  }
  return options.sort((left, right) => left.value.localeCompare(right.value));
}

// `key:value` → 라벨 색이다. facet 드롭다운이 담당하는 예약 라벨(task·environment·source)도
// 카탈로그 라벨이라 색이 있다. 추천 후보 행의 칩은 facet 값에도 그 색을 적용한다. 라벨이 아닌
// 축(embodiment·tip·팔 구성·카메라)은 여기 없으므로 중립색으로 그린다.
export function createLabelColorIndex(labels: readonly Label[]): ReadonlyMap<string, number> {
  const index = new Map<string, number>();
  for (const label of labels) {
    index.set(`${label.key}:${label.value}`, label.color);
  }
  return index;
}

// 라벨 조건의 표시 라벨이다. 후보 행과 적용 조건 칩이 같은 표기를 쓴다.
export function labelTokenDisplay(key: string, value: string): string {
  return `${formatFilterLabel(key)}: ${value}`;
}

// 라벨 토큰 값("key:value")을 key/value 로 나눈다. 라벨 key 는 식별자라 콜론이 없다. 그래서 첫 콜론에서 자른다.
export function splitLabelToken(tokenValue: string): [string, string] | null {
  const separator = tokenValue.indexOf(":");
  if (separator < 0) {
    return null;
  }
  return [tokenValue.slice(0, separator), tokenValue.slice(separator + 1)];
}

// 카메라 채널 수 필터는 0~6 의 고정 옵션을 제공한다. 백엔드는 실측 채널 수로 거른다.
// 라벨은 "N ch" 로 표시한다. 0 은 채널이 하나도 없는 Dataset 이라 "없음" 으로 적는다. 0 은 카메라
// 축(Camera Layout 포함)의 미지정을 고르는 유일한 토큰이다.
const CAMERA_COUNT_FACET_PRESETS = ["0", "1", "2", "3", "4", "5", "6"] as const;
const NO_CAMERA_COUNT = "0";

// Embodiment 카탈로그에서 하드웨어 facet 그룹(embodiment / end_effector)을 만든다.
// 팔 구성(arms)은 카탈로그 선언이 아니라 데이터셋 실측 채널로 판정한다. 그래서 여기서 만들지
// 않고 armLayoutFacetGroup(고정 vocab)이 만든다. camera_channel("Camera Layout")도 실측 채널
// 인벤토리에서 만든다. createCameraChannelFacetGroup(distinct-channel 엔드포인트)이 담당한다.
export function createEmbodimentFacetGroups(
  embodiments: readonly Embodiment[],
): readonly FilterGroup[] {
  const names = new Set<string>();
  const endEffectors = new Set<string>();
  for (const embodiment of embodiments) {
    if (embodiment.name) {
      names.add(embodiment.name);
    }
    if (embodiment.end_effector?.name) {
      endEffectors.add(embodiment.end_effector.name);
    }
  }
  return [
    catalogFacetGroup("embodiment", "Embodiment", names, "하드웨어가 미상인 Dataset입니다."),
    catalogFacetGroup(
      "end_effector",
      "End effector",
      endEffectors,
      "End effector가 미상인 Dataset입니다.",
    ),
  ].filter((group) => group.filters.length > 0);
}

// Camera Layout(camera_channel) facet 이다. 데이터셋 실측 카메라 채널명(wrist_upper/wrist_left…)의 union 이다.
// 옵션 vocab 은 listDatasetCameraChannels(distinct-channel 엔드포인트) 결과가 SoT 다.
export function createCameraChannelFacetGroup(channels: readonly string[]): readonly FilterGroup[] {
  const values = new Set(channels.filter((channel) => channel !== ""));
  return [facetGroupFromValues("camera_channel", "Camera Layout", values)].filter(
    (group) => group.filters.length > 0,
  );
}

// 카메라 채널 수(camera_count) 프리셋 facet 이다. 하드코딩한 0~6 을 항상 반환한다.
// 백엔드가 실측 채널 수로 거르고, 값은 "N ch" 로 표기한다.
export function createCameraCountFacetGroup(): readonly FilterGroup[] {
  return [
    {
      value: "camera_count",
      label: "카메라 수",
      filters: CAMERA_COUNT_FACET_PRESETS.map(cameraCountOption),
    },
  ];
}

// 카메라 수 옵션 하나다. facets 가 6 보다 큰 값을 보내면 개수 적용 코드가 같은 모양으로 추가한다.
export function cameraCountOption(value: string): FilterOption {
  if (value === NO_CAMERA_COUNT) {
    return { value, label: "없음", description: "카메라 채널이 하나도 없는 Dataset입니다." };
  }
  return {
    value,
    label: `${value} ch`,
    description: facetOptionDescription("camera_count", value),
  };
}

// Tip 카탈로그에서 tip facet 그룹을 만든다. EE 와 독립인 말단 tip 축이고, 값은 Dataset.tip_id 하나다.
export function createTipFacetGroups(tips: readonly Tip[]): readonly FilterGroup[] {
  const names = new Set<string>();
  for (const tip of tips) {
    if (tip.name) {
      names.add(tip.name);
    }
  }
  return [
    catalogFacetGroup("tip", "Tip", names, "Tip이 미상이거나 고르지 않은 Dataset입니다."),
  ].filter((group) => group.filters.length > 0);
}

// 카탈로그 축(embodiment · end_effector · tip)이다. 이름 목록 뒤에 `미상` 을 둔다. 카탈로그에는
// sentinel 행 `unknown` 이 이름과 섞여 온다. 이 토큰은 이름이 아니라 "하드웨어를 모른다" 는 미지정
// 묶음이다(tip 은 미선택 NULL 도 함께 고른다). 그래서 이름 사이에서 빼고 고정 위치에 둔다.
// 카탈로그가 비었으면 축을 만들지 않는다.
function catalogFacetGroup(
  key: string,
  label: string,
  names: ReadonlySet<string>,
  unknownDescription: string,
): FilterGroup {
  const unknownToken = DATASET_FACET_UNSPECIFIED_TOKENS[key];
  const known = new Set([...names].filter((name) => name !== unknownToken));
  const group = facetGroupFromValues(key, label, known);
  if (names.size === 0) {
    return group;
  }
  return {
    ...group,
    filters: [
      ...group.filters,
      { value: unknownToken, label: "미상", description: unknownDescription },
    ],
  };
}

// 라벨 축(source · environment · task)의 `미지정` 이다. 그 key 의 라벨이 없는 Dataset 을
// 고르는 조회 토큰이다. 값이 하나도 없는 축에는 추가하지 않는다. 추가하면 축이 생겨서 프리셋이
// 동작하지 않는 상태로 활성화된다(`isPresetAvailable`).
function withUnlabeledOption(group: FilterGroup): FilterGroup {
  if (group.filters.length === 0) {
    return group;
  }
  return {
    ...group,
    filters: [
      ...group.filters,
      {
        value: DATASET_FACET_UNSPECIFIED_TOKENS[group.value],
        label: "미지정",
        description: "이 항목의 라벨이 붙지 않은 Dataset입니다.",
      },
    ],
  };
}

// vocab 문자열로만 만드는 facet(embodiment·tip·camera_*·source)의 설명 줄이다. 여기서는
// 서버 라벨 행이 없어서 FE 가이드가 유일한 출처다. 라벨 행이 있는 facet 은 아래
// `createLabelFilterGroups` 처럼 `labelExplanation`(서버 설명 우선)을 쓴다.
function facetOptionDescription(key: string, value: string): string {
  return labelGuideSummary(key, value) ?? `${key}:${value}`;
}

function facetGroupFromValues(
  key: string,
  label: string,
  values: ReadonlySet<string>,
): FilterGroup {
  return {
    value: key,
    label,
    filters: Array.from(values)
      .sort((left, right) => left.localeCompare(right))
      .map((value) => ({
        value,
        label: formatFilterLabel(value),
        description: facetOptionDescription(key, value),
      })),
  };
}

// 데이터 소스(source) facet 의 표시 순서다. 수집 주체 reserved allowlist 다. vocab 의 SoT 는 서버
// reserved 계약이다(sourceLabelValues 가 그 스냅샷을 읽는다). 표시 라벨(한글)은 labelGuide 가
// 소유해 칩·목록·필터 문구를 통일한다. 여기서는 표시 순서만 정한다.
const SOURCE_VALUE_ORDER: readonly string[] = ["internal", "customer", "alba", "open_source"];

// 데이터 소스 facet 이다. source reserved allowlist 를 쓴다. 고정 vocab 이라 데이터 유무와 관계없이
// 항상 표시한다(다중선택). 값 순서는 SOURCE_VALUE_ORDER 를 먼저 따른다. vocab 에만 있는 값은
// 뒤에 추가해 빠지지 않게 한다.
function sourceFacetGroup(): FilterGroup {
  const known = sourceLabelValues();
  const knownSet = new Set(known);
  const ordered = [
    ...SOURCE_VALUE_ORDER.filter((value) => knownSet.has(value)),
    ...known.filter((value) => !SOURCE_VALUE_ORDER.includes(value)),
  ];
  return withUnlabeledOption({
    value: "source",
    label: "데이터 소스",
    filters: ordered.map((value) => ({
      value,
      label: labelValueText("source", value),
      description: facetOptionDescription("source", value),
    })),
  });
}

// 에피소드 결함(episode_quality) facet 이다. 에피소드 리뷰가 붙이는 결함 태그로 데이터셋을 고른다.
// 그 태그가 붙은 에피소드가 하나라도 있으면 결과에 남는다. 전역 제외된 에피소드의 결함은 세지 않는다.
// BE 는 이 축을 `?episode_quality=` 로 받는다.
//
// 선택지는 에피소드 목록 필터와 같은 빌더(`episodeTagChoices`)로 만든다. 순서는 `결함 있음`·`결함 없음`
// → 갈래 행 + 소속 값이다. 서버가 값·갈래·토큰을 한 축 OR 로 받으므로 드롭다운 하나로 둔다. 갈래 행
// 값은 `category:<code>` 라서 `createLabelFilterQuery` 가 `episode_quality_category` 로 분리해 보낸다.
//
// 어휘는 호출자가 review-options 의 `episode_quality_tags` 를 전달한다. FE 폴백 어휘는 없다. `EPISODE_QUALITY_TAG_LABELS` 는 더 이상 쓰지 않는 키도 포함하므로 폴백으로 쓸 수 없다. 어휘가
// 비어 있으면 그룹을 만들지 않아 축이 표시되지 않는다. 갈래와 소속은 `label-categories?key=quality` 가
// 준다. 받지 못했으면(null) 갈래 행 없이 값만 표시한다. 표기는 에피소드 화면의 기준인 `한글(영어)` 다.
export function createEpisodeQualityFacetGroup(
  tags: readonly string[],
  categories: readonly LabelCategory[] | null = null,
): readonly FilterGroup[] {
  if (tags.length === 0) {
    return [];
  }
  const choices = episodeTagChoices({
    tags: tags.map((value) => ({ value })),
    tagText: episodeQualityTagText,
    tagTip: (tag) => episodeQualityTagTip(tag) ?? tag,
    any: {
      label: "결함 있음",
      description: "결함 에피소드가 하나라도 있는 Dataset입니다.",
    },
    none: {
      label: "결함 기록 없음",
      description: "결함 기록 없음은 정상 판정을 뜻하지 않습니다.",
    },
    categories: (categories ?? []).map((category) => ({
      code: category.code,
      displayName: category.display_name,
    })),
    membership:
      categories === null
        ? new Map()
        : new Map(categories.map((category) => [category.code, category.values] as const)),
  });
  return [{ value: "episode_quality", label: "에피소드 결함", filters: choices }];
}

// 수집 방식(recording_mode) facet 값이다. 백엔드에서 **두 컬럼을 쓰는 한 축**이다. 구조에서 계산한
// `recording_mode` 가 teleop / DT / 미판정을 구분한다. 업로더가 선언하는 `data_origin` 이
// 추론 롤아웃을 구분한다. 네 토큰은 겹치지 않는다. 구조 토큰에서 롤아웃을 빼므로 같은
// 데이터셋이 두 버킷에 들어가지 않는다.
//
// 롤아웃은 구조로 판정할 수 없어서 이렇게 나눈다. 정책이 만든 action 도 commanded 채널로 들어오므로
// 구조만 보면 teleop 과 구분할 수 없다. 그래서 `inference` 는 컬럼 값이 아니라 facet 토큰이다.
const RECORDING_MODE_OPTIONS: readonly { value: string; label: string; description: string }[] = [
  {
    value: "teleoperation",
    label: "원격조작 (Teleop)",
    description: "입력 장치로 로봇을 조종해 수집했습니다.",
  },
  {
    value: "direct_teaching",
    label: "Direct Teaching (DT)",
    description: "사람이 로봇 팔을 직접 움직여 수집했습니다.",
  },
  {
    value: "inference",
    label: "Inference Rollout",
    description: "학습된 Policy 를 실행해 얻은 데이터입니다. 사람 시연이 아닙니다.",
  },
  {
    value: "unknown",
    label: "미판정",
    description: "채널 구조만으로 수집 방식을 확정하지 못했습니다.",
  },
  {
    value: "not_applicable",
    label: "해당 없음",
    description: "외부 출처 데이터라 수집 방식 규격이 없습니다.",
  },
];

// `data_origin = policy_rollout` 을 고르는 facet 토큰이다. 컬럼 값이 아니라 조회 어휘라서
// PATCH 로 보내면 422 가 된다.
const INFERENCE_RECORDING_MODE_TOKEN = "inference";

// 미판정(`Dataset.arm_layout` 의 NULL)을 고르는 facet 토큰이다. recording_mode 의 `unknown` 과
// 같은 규약이다. 컬럼 값이 아니라 조회 어휘다.
const UNKNOWN_ARM_LAYOUT_TOKEN = "unknown";

// 팔 구성(arms) facet 값이다. 데이터셋 실측 채널 이름의 좌우 방향으로 판정하는 축이다. admin 이
// 선언하지 않으므로 값은 카탈로그가 아니라 이 목록이 소유한다.
const ARM_LAYOUT_OPTIONS: readonly { value: string; label: string; description: string }[] = [
  {
    value: "single_arm",
    label: "Single Arm",
    description: "채널 이름 어디에도 좌우 방향이 없습니다.",
  },
  {
    value: "bimanual",
    label: "Bimanual",
    description: "채널 이름에 좌우가 모두 있습니다.",
  },
  {
    value: UNKNOWN_ARM_LAYOUT_TOKEN,
    label: "미판정",
    description: "한쪽 방향만 있거나 채널이 없어 팔 구성을 확정하지 못했습니다.",
  },
];

// 팔 구성 facet 이다. 실측 판정 enum 과 unknown 토큰이라서 카탈로그 데이터와 관계없이 항상 로드한다.
// 컨트롤은 렌더하지 않고 프리셋(양팔/한팔)으로만 조작한다(CURATED_FACETS 의 section 참조).
function armLayoutFacetGroup(): FilterGroup {
  return {
    value: "arms",
    label: "팔 구성",
    filters: ARM_LAYOUT_OPTIONS.map((option) => ({ ...option })),
  };
}

// 팔 구성 값의 표시 라벨이다. 목록 표·카드·제출 picker·채널 구조 카드가 같은 문구를 쓰도록
// 여기서만 정한다. 미판정은 null 이다. 호출부가 위치에 맞는 표기를 고른다(표 셀은 `-`,
// 축을 지우면 안 되는 곳은 "미판정").
export function formatArmLayout(layout: ArmLayout | null | undefined): string | null {
  if (!layout) {
    return null;
  }
  return ARM_LAYOUT_OPTIONS.find((option) => option.value === layout)?.label ?? layout;
}

// 수집 방식 facet 이다. 백엔드 enum 과 unknown/inference 토큰이라서 카탈로그 데이터와 관계없이 항상
// 표시한다. 다중선택이다. teleop 과 DT 를 함께 골라 "판정된 것만" 보는 조회가 필요하기 때문이다.
function recordingModeFacetGroup(): FilterGroup {
  return {
    value: "recording_mode",
    label: "수집 방식",
    filters: RECORDING_MODE_OPTIONS.map((option) => ({ ...option })),
  };
}

// 카탈로그 데이터에 의존하지 않는 고정 vocab facet 그룹이다(source·recording_mode·arms·action_source).
// 순서는 selectCuratedFacetGroups 가 CURATED_FACETS 로 정한다. 그래서 여기의 순서는 결과에 영향이 없다.
export function createStaticFacetGroups(): readonly FilterGroup[] {
  return [
    {
      value: "episode_quality_candidate",
      label: "미처리 품질 후보",
      display: "select",
      filters: [
        {
          value: "pending",
          label: "있음",
          description: "제외되지 않은 에피소드에 아직 처리하지 않은 자동 품질 후보가 있습니다.",
        },
        {
          value: "none",
          label: "없음",
          description: "미분석·후보 없음·처리 완료 포함 (정상 판정 아님)",
        },
      ],
    },
    sourceFacetGroup(),
    recordingModeFacetGroup(),
    armLayoutFacetGroup(),
    { value: "action_source", label: "행동 출처", filters: ACTION_SOURCE_OPTIONS },
  ];
}

// 수집 방식 값의 표시 라벨이다. 필터 드롭다운·상세 화면·카드 파생 칩이 같은 문구를 쓰도록
// 여기서만 정한다.
export function formatRecordingMode(
  value: string | null | undefined,
  dataOrigin?: string | null,
): string | null {
  const resolved = resolveRecordingModeToken(value, dataOrigin);
  if (resolved === null) {
    return null;
  }
  const option = RECORDING_MODE_OPTIONS.find((candidate) => candidate.value === resolved);
  return option ? option.label : formatFilterLabel(resolved);
}

// 리뷰어가 "해당 없음" 으로 지정한 수집 방식의 표시다. teleop 도 DT 도 아닌 외부 데이터다.
export const NOT_APPLICABLE_RECORDING_MODE_LABEL = "해당 없음";

// 지정 여부까지 반영한 수집 방식 표시다. 리뷰어가 지정한 null 은 미판정이 아니라 "해당 없음" 이다.
// 그래서 같은 null 을 두 문구로 구분해야 한다. 그 분기를 여기 한 곳에 둔다. 미판정은 null 을
// 반환하고, 호출자가 빈 표시(`—` / `미판정`)를 고른다.
export function formatDeclarableRecordingMode(
  value: string | null | undefined,
  dataOrigin: string | null | undefined,
  declared: boolean,
): string | null {
  const label = formatRecordingMode(value, dataOrigin);
  if (label !== null) {
    return label;
  }
  return declared ? NOT_APPLICABLE_RECORDING_MODE_LABEL : null;
}

// 수집 방식 값의 사유 문구다. 라벨 축의 `labelGuideSummary` 와 같은 위치(칩 툴팁·facet 설명)에
// 쓴다. vocab 에 없는 값은 null 이고, 호출자가 라벨 텍스트로 대신한다.
export function recordingModeGuide(
  value: string | null | undefined,
  dataOrigin?: string | null,
): string | null {
  const resolved = resolveRecordingModeToken(value, dataOrigin);
  if (resolved === null) {
    return null;
  }
  return (
    RECORDING_MODE_OPTIONS.find((candidate) => candidate.value === resolved)?.description ?? null
  );
}

// 표시에 쓸 수집 방식 토큰을 확정한다. 라벨과 사유가 같은 값을 쓰게 하는 유일한 지점이다.
//
// `dataOrigin` 이 롤아웃이면 구조에서 계산한 값 대신 `inference` 를 쓴다. facet 은 네 토큰이 겹치지
// 않게 둔다. 바꾸지 않으면 "추론 롤아웃"으로 필터한 데이터셋의 상세가 "원격조작"으로 표시되는
// 모순이 생긴다. 롤아웃의 구조 계산값은 teleop 으로 잘못 판정된 값이다.
function resolveRecordingModeToken(
  value: string | null | undefined,
  dataOrigin?: string | null,
): string | null {
  const resolved = dataOrigin === "policy_rollout" ? INFERENCE_RECORDING_MODE_TOKEN : value;
  return resolved ? resolved : null;
}

// 필터 바에서 라벨 카탈로그로 로드할 라벨 facet key 다(environment/task). environment 는 컨트롤을
// 렌더하지 않고 프리셋(RealWorld/시뮬레이션)으로만 조작한다. 그러나 프리셋 선택을 유지하려면 그룹을
// 로드해야 하므로 포함한다. source·recording_mode 는 서버 계약이 vocab 을 소유하는 고정
// vocab 정적 그룹이다. generic 라벨은 라벨 토큰 검색이 담당한다.
const LABEL_FACET_KEYS: ReadonlySet<string> = new Set(["environment", "task"]);

export function createDatasetLabelFilterGroups(labels: readonly Label[]): readonly FilterGroup[] {
  const labelsByKey = new Map<string, Map<string, Label>>();

  for (const label of labels) {
    if (!LABEL_FACET_KEYS.has(label.key)) {
      continue;
    }
    const labelsByValue = getOrCreateLabelValueMap(labelsByKey, label.key);
    if (!labelsByValue.has(label.value)) {
      labelsByValue.set(label.value, label);
    }
  }

  return Array.from(labelsByKey.entries())
    .map(([key, labelsByValue]) =>
      withUnlabeledOption({
        value: key,
        label: formatFilterLabel(key),
        // environment(데이터 소스)와 task 모두 dropdown 이다. environment 는 시안에서 기본 축 드롭다운
        // ("데이터 소스")으로 표시한다(real/simulation 다중선택 가능). 선택지별 수는 여기가 아니라
        // facets 가 폴더 단위로 준다(`applyDatasetFacetCounts`). 라벨 카탈로그의 `dataset_count` 는
        // 폴더를 모르고 세션 캐시라서 업로드 뒤에도 갱신되지 않는다.
        filters: Array.from(labelsByValue.values())
          .sort((left, right) => left.value.localeCompare(right.value))
          .map((label) => ({
            value: label.value,
            label: formatFilterLabel(label.value),
            description: labelExplanation(label) ?? `${label.key}:${label.value}`,
          })),
      }),
    )
    .filter((group) => group.filters.length > 0);
}

function getOrCreateLabelValueMap(
  labelsByKey: Map<string, Map<string, Label>>,
  key: string,
): Map<string, Label> {
  const existing = labelsByKey.get(key);
  if (existing !== undefined) {
    return existing;
  }

  const nextMap = new Map<string, Label>();
  labelsByKey.set(key, nextMap);
  return nextMap;
}

function formatFilterLabel(value: string): string {
  return value
    .split("_")
    .map((part) => (part.length === 0 ? part : part[0].toUpperCase() + part.slice(1)))
    .join(" ");
}
